/**
 * Structured logging utility for ludicrous-gateway
 * Provides consistent log formatting and levels
 */

export type LogLevel = "debug" | "info" | "warn" | "error";
export type LogFormat = "text" | "json";

/**
 * Log entry structure
 */
export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

/**
 * Logger configuration
 */
export interface LoggerConfig {
  level: LogLevel;
  format: LogFormat;
}

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

const LOG_LEVEL_COLORS: Record<LogLevel, string> = {
  debug: "\x1b[36m", // Cyan
  info: "\x1b[32m", // Green
  warn: "\x1b[33m", // Yellow
  error: "\x1b[31m", // Red
};

const RESET_COLOR = "\x1b[0m";

/**
 * Logger class for structured logging
 */
export class Logger {
  private level: LogLevel;
  private format: LogFormat;
  private context: Record<string, unknown>;

  constructor(config: LoggerConfig, context: Record<string, unknown> = {}) {
    this.level = config.level;
    this.format = config.format;
    this.context = context;
  }

  /**
   * Create a child logger with additional context
   */
  child(context: Record<string, unknown>): Logger {
    return new Logger(
      { level: this.level, format: this.format },
      { ...this.context, ...context }
    );
  }

  /**
   * Check if a log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.level];
  }

  /**
   * Format and output a log entry
   */
  private log(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>
  ): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context: { ...this.context, ...context },
    };

    if (this.format === "json") {
      this.outputJson(entry);
    } else {
      this.outputText(entry);
    }
  }

  /**
   * Output log entry as JSON
   */
  private outputJson(entry: LogEntry): void {
    const output = {
      level: entry.level,
      time: entry.timestamp,
      msg: entry.message,
      ...entry.context,
    };
    console.log(JSON.stringify(output));
  }

  /**
   * Output log entry as formatted text
   */
  private outputText(entry: LogEntry): void {
    const color = LOG_LEVEL_COLORS[entry.level];
    const levelStr = entry.level.toUpperCase().padEnd(5);
    const time = entry.timestamp.split("T")[1]?.slice(0, 12) ?? entry.timestamp;

    let line = `${color}${levelStr}${RESET_COLOR} [${time}] ${entry.message}`;

    if (entry.context && Object.keys(entry.context).length > 0) {
      const contextStr = Object.entries(entry.context)
        .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
        .join(" ");
      line += ` ${contextStr}`;
    }

    console.log(line);
  }

  /**
   * Log a debug message
   */
  debug(message: string, context?: Record<string, unknown>): void {
    this.log("debug", message, context);
  }

  /**
   * Log an info message
   */
  info(message: string, context?: Record<string, unknown>): void {
    this.log("info", message, context);
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: Record<string, unknown>): void {
    this.log("warn", message, context);
  }

  /**
   * Log an error message
   */
  error(message: string, context?: Record<string, unknown>): void {
    this.log("error", message, context);
  }
}

// Default logger instance
let defaultLogger: Logger = new Logger({ level: "info", format: "text" });

/**
 * Initialize the default logger
 */
export function initLogger(config: LoggerConfig): void {
  defaultLogger = new Logger(config);
}

/**
 * Get the default logger
 */
export function getLogger(): Logger {
  return defaultLogger;
}

/**
 * Create a child logger with context
 */
export function createLogger(context: Record<string, unknown>): Logger {
  return defaultLogger.child(context);
}
